import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { timezones } from '../../../../constants/timezones';
import {
    limitsStateValues,
    initialValues,
    PrizeRatioType,
} from '../../../../types/componentTypes/limits';
import { ConfigType } from '../../../../types/configuration';
import {
    getIWLimitsForConfig,
    validateCheckerLambdas,
} from '../helpers/limit-helpers';
import { flowLabelMap } from '../../../../constants/lists';
import { prizeDataType } from '../../EditPromotionPage';

const useLimitsInitialState = (config: ConfigType, prizesData?: Array<prizeDataType>) => {
    const [initialState, setInitialState] =
        useState<limitsStateValues>(initialValues);
    const [promoFlow, setPromoFlow] = useState<string>('');

    // ratio winning state
    const [prizeRatios, setPrizeRatios] = useState<Array<PrizeRatioType>>([]);

    // ratio winning dropdown options state
    const [prizeOptions, setPrizeOptions] = useState<Array<string>>([]);

    useEffect(() => {
        const flows = Object.values(flowLabelMap);
        const foundFlow = flows.find(flowName => config.flow?.[flowName]);
        if (foundFlow) setPromoFlow(foundFlow);
    },
        [config]);

    const getPrizeName = (prize: prizeDataType, languageCode: string) => {
        const prizeName = JSON.parse(JSON.stringify(prize.name));
        return JSON.parse(prizeName)[languageCode];
    };

    const processPrizeData = useCallback((prizesData: prizeDataType[], language: string) => {
        const createOptions = (currPrize) => {
            if (!currPrize.active || !currPrize.pool_prize) return null;
            return `${getPrizeName(currPrize, language)} / ${currPrize.prize_id}`;
        }

        const createData = (currPrize) => {
            if (!currPrize.winning_ratio) return null;
            return {
                prizeId: `${getPrizeName(currPrize, language)} / ${currPrize.prize_id}`,
                ratio: currPrize.winning_ratio / 100,
            };
        }

        return prizesData.reduce((acc: { options: string[], data: PrizeRatioType[] }, currPrize) => {
            const option = createOptions(currPrize);
            const datum = createData(currPrize);

            if (option) acc.options.push(option);
            if (datum) acc.data.push(datum);

            return acc;
        }, { options: [], data: [] })
    }, []);

    useEffect(() => {
        if (!prizesData.length) return;

        const language = config.configurationParameters.language;
        const { options, data } = processPrizeData(prizesData, language);

        setPrizeRatios(data.length ? data : [{ prizeId: "", ratio: 0 }]);
        setPrizeOptions(options);
    },
        [prizesData, config.configurationParameters.language, processPrizeData])

    useEffect(() => {
        const flowParams = config.flow?.[promoFlow]?.params || {};
        const configParams = config.configurationParameters || {};

        const updatedState: limitsStateValues = {
            ...config,
            tempParams: {
                limits: {
                    ...initialValues.tempParams.limits,
                    minAge: !!flowParams?.minAge,
                    lifetime: !!flowParams?.participationLifetimeLimit,
                    rolling: !!flowParams?.participationLimit,
                    calendarDay: !!flowParams?.participationLimitCalendarDatesLimit,
                    calendarCycle: !!flowParams?.participationCalendarCycleLimit,
                },
                alwaysWinLimits: {
                    ...initialValues.tempParams.alwaysWinLimits,
                    prizeOptions: prizeOptions,
                },
                ...(promoFlow === 'instantWin' &&
                {
                    instantWinCalendarCycleLimitFlag: !!flowParams?.instantWinCalendarCycleLimit,
                    winningLimitsPerTierFlag: flowParams?.winningLimitsPerTier ? true : false,
                    instantWinLimitPerHourFlag: flowParams?.instantWinLimitPerTimeInterval ? true : false,
                    ...getIWLimitsForConfig(config)
                }),
                ...((promoFlow === 'autoRedeemCnG' || promoFlow === 'redeemPincodeForCurrencies') && {
                    redemptionLimitsPerTierFlag: flowParams?.redemptionLimitsPerTier ? true : false,
                }),
                startDateTime: configParams?.configurationStartUtc,
                endDateTime: configParams?.configurationEndUtc,
            },
            flow: {
                ...config.flow,
                [promoFlow]: {
                    ...config.flow[promoFlow],
                    params: { ...flowParams },
                    checkerLambdas: (promoFlow === 'instantWin' ? validateCheckerLambdas(config, promoFlow) : config.flow[promoFlow]?.checkerLambdas)
                },
            },
            participationLimits: {
                participationLimit: flowParams?.participationLimit || 1,
                participationLimitTime: flowParams?.participationLimitTime || 1,
                participationLimitTimeZone:
                    (flowParams?.participationLimitTimeZone &&
                        timezones[
                        flowParams?.participationLimitTimeZone
                        ]) ||
                    (configParams?.configurationDatesTimezone &&
                        timezones[
                        configParams?.configurationDatesTimezone
                        ]) ||
                    timezones[moment.tz.guess()] ||
                    '',
                participationLimitCalendarDatesRange: {
                    startDate:
                        flowParams
                            ?.participationLimitCalendarDatesRange?.startDate ||
                        configParams?.configurationStartUtc ||
                        parseInt(moment().format('x')),
                    endDate:
                        flowParams
                            ?.participationLimitCalendarDatesRange?.endDate ||
                        configParams?.configurationEndUtc ||
                        parseInt(moment().add(1, 'days').format('x')),
                },
                participationLimitMultipleChecks:
                    !!flowParams?.participationLimitMultipleChecks,
                participationLifetimeLimit:
                    flowParams?.participationLifetimeLimit || 1,
                participationLimitStartEndDatesRange: {
                    startDate:
                        flowParams
                            ?.participationLimitStartEndDatesRange?.startDate ||
                        configParams?.configurationStartUtc ||
                        parseInt(moment().format('x')),
                    endDate:
                        flowParams
                            ?.participationLimitStartEndDatesRange?.endDate ||
                        configParams?.configurationEndUtc ||
                        parseInt(moment().add(1, 'days').format('x')),
                },
                participationLimitCalendarDatesLimit:
                    flowParams
                        ?.participationLimitCalendarDatesLimit || 1,
                participationLimitCalendarDatesTimeZone:
                    (flowParams
                        ?.participationLimitCalendarDatesTimeZone &&
                        timezones[
                        flowParams
                            ?.participationLimitCalendarDatesTimeZone
                        ]) ||
                    (configParams?.configurationDatesTimezone &&
                        timezones[
                        configParams?.configurationDatesTimezone
                        ]) ||
                    timezones[moment.tz.guess()] ||
                    '',
                participationCalendarCycleLimit: {
                    limit: flowParams?.participationCalendarCycleLimit?.limit || 1,
                    period: flowParams?.participationCalendarCycleLimit?.period || 'week',
                    firstDayOfWeek: flowParams?.participationCalendarCycleLimit?.firstDayOfWeek || 'monday',
                },
                minAge: flowParams?.minAge || 12,
            },
            alwaysWinLimits: {
                alwaysWinPrizeRatios: prizeRatios,
                ratioWinning: flowParams?.ratioWinning,
            },
            winningLimits: {
                instantWinCalendarCycleLimit: {
                    limit: flowParams?.instantWinCalendarCycleLimit?.limit || 1,
                    period: flowParams?.instantWinCalendarCycleLimit?.period || 'week',
                    firstDayOfWeek: flowParams?.instantWinCalendarCycleLimit?.firstDayOfWeek || 'monday',
                }
            },
            selectedDateLimitOption:
                moment(configParams?.configurationStartUtc).isSame(
                    flowParams?.participationLimitStartEndDatesRange
                        ?.startDate
                ) &&
                    moment(configParams?.configurationEndUtc).isSame(
                        flowParams?.participationLimitStartEndDatesRange
                            ?.endDate
                    )
                    ? 'useConfigStartEndDates'
                    : 'participationLimitStartEndDatesRange',
        };
        setInitialState(updatedState);
    }, [config, promoFlow, prizeOptions, prizeRatios]);

    return {
        initialState
    }
};

export default useLimitsInitialState;
